import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, CheckCircle, AlertTriangle, Eye, Info } from 'lucide-react';
import { api } from '../services/api';
import type { AdminOrg, ReprocessResult, PreviewMeeting } from '../types';
import { fmtDate } from '../utils/format';

function NoteTag({ note }: { note: string | null }) {
  if (!note) {
    return (
      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#1e293b', color: '#f59e0b', fontWeight: 600 }}>
        SILENT FAILURE
      </span>
    );
  }
  if (note.startsWith('AI extraction failed:')) {
    return (
      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#1e1b4b', color: '#f87171', fontWeight: 600 }}>
        AI ERROR
      </span>
    );
  }
  return (
    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#14532d', color: '#4ade80', fontWeight: 600 }}>
      LEGIT EMPTY
    </span>
  );
}

export default function ReprocessPage() {
  const [searchParams] = useSearchParams();
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(searchParams.get('orgId') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewMeetings, setPreviewMeetings] = useState<PreviewMeeting[] | null>(null);
  const [previewOrgName, setPreviewOrgName] = useState('');
  const [history, setHistory] = useState<(ReprocessResult & { ts: string })[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/admin/orgs')
      .then(r => setOrgs(r.data.orgs))
      .finally(() => setOrgsLoading(false));
  }, []);

  // Clear preview when org changes
  useEffect(() => {
    setPreviewMeetings(null);
    setPreviewOrgName('');
  }, [selectedOrgId]);

  const filteredOrgs = useMemo(() => {
    if (!search.trim()) return orgs;
    const q = search.toLowerCase();
    return orgs.filter(o => o.name.toLowerCase().includes(q) || o.ownerEmail.toLowerCase().includes(q));
  }, [orgs, search]);

  const selectedOrg = orgs.find(o => o.orgId === selectedOrgId);

  const handlePreview = async () => {
    if (!selectedOrgId) return;
    setPreviewing(true);
    try {
      const r = await api.post(`/admin/orgs/${selectedOrgId}/reprocess?preview=1`);
      setPreviewMeetings(r.data.meetings ?? []);
      setPreviewOrgName(r.data.orgName ?? selectedOrgId);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Preview failed';
      setPreviewMeetings(null);
      alert(`Preview error: ${msg}`);
    } finally {
      setPreviewing(false);
    }
  };

  const handleReprocess = async () => {
    if (!selectedOrgId) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/orgs/${selectedOrgId}/reprocess`);
      setHistory(prev => [{ ...r.data, ts: new Date().toISOString() }, ...prev].slice(0, 10));
      setPreviewMeetings(null); // clear preview after running
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Request failed';
      setHistory(prev => [{ reset: -1, orgId: selectedOrgId, message: msg, ts: new Date().toISOString() }, ...prev].slice(0, 10));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    color: 'var(--text)',
    fontSize: 13,
    outline: 'none',
  };

  const silentCount = previewMeetings?.filter(m => !m.extractionNote).length ?? 0;
  const aiErrCount = previewMeetings?.filter(m => m.extractionNote?.startsWith('AI extraction failed:')).length ?? 0;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <RefreshCw size={18} color="var(--warning)" />
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Meeting Reprocess</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24, lineHeight: 1.6 }}>
        Finds meetings where <code style={{ background: 'var(--bg-card)', padding: '1px 5px', borderRadius: 3 }}>processed=true</code>,{' '}
        <code style={{ background: 'var(--bg-card)', padding: '1px 5px', borderRadius: 3 }}>tasksExtracted=0</code>,
        and either no <code style={{ background: 'var(--bg-card)', padding: '1px 5px', borderRadius: 3 }}>extractionNote</code> (silent failure)
        or an <code style={{ background: 'var(--bg-card)', padding: '1px 5px', borderRadius: 3 }}>"AI extraction failed:…"</code> note (recorded error) — and resets them. A new sync runs automatically.
      </p>

      {/* Diagnostics legend */}
      <div style={{
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        marginBottom: 20,
        padding: '10px 14px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        fontSize: 12,
        color: 'var(--text-muted)',
        flexWrap: 'wrap',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Info size={13} color="var(--text-dim)" />
          Meeting categories:
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NoteTag note={null} /> No extractionNote — processing ran but nothing was recorded (pre-fix)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NoteTag note="AI extraction failed: …" /> Claude API call failed — error message stored in extractionNote
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NoteTag note="Pure status update" /> Legitimate empty — Claude confirmed no tasks; won't be reprocessed
        </span>
      </div>

      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 24,
        marginBottom: 24,
      }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
            Filter orgs
          </label>
          <input
            type="text"
            placeholder="Search by name or owner…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
            Select org to reprocess
          </label>
          <select
            value={selectedOrgId}
            onChange={e => setSelectedOrgId(e.target.value)}
            disabled={orgsLoading}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">— Select an org —</option>
            {filteredOrgs.map(o => (
              <option key={o.orgId} value={o.orgId}>
                {o.name} ({o.ownerEmail}) · {o.plan}
              </option>
            ))}
          </select>
        </div>

        {selectedOrg && (
          <div style={{
            padding: '10px 14px',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--text-muted)',
            marginBottom: 20,
            display: 'flex',
            gap: 20,
            flexWrap: 'wrap',
          }}>
            <span><strong>Plan:</strong> {selectedOrg.plan}</span>
            <span><strong>Members:</strong> {selectedOrg.memberCount}</span>
            <span><strong>Meetings this month:</strong> {selectedOrg.meetingsProcessedThisMonth}</span>
            <span><strong>orgId:</strong> <code style={{ fontFamily: 'monospace', fontSize: 10 }}>{selectedOrg.orgId}</code></span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handlePreview}
            disabled={!selectedOrgId || previewing || submitting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 18px',
              background: 'var(--bg)',
              color: (!selectedOrgId || previewing) ? 'var(--text-dim)' : 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: (!selectedOrgId || previewing || submitting) ? 'not-allowed' : 'pointer',
            }}
          >
            <Eye size={14} style={previewing ? { animation: 'spin 1s linear infinite' } : {}} />
            {previewing ? 'Previewing…' : 'Preview'}
          </button>

          <button
            onClick={handleReprocess}
            disabled={!selectedOrgId || submitting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              background: (!selectedOrgId || submitting) ? 'var(--accent-subtle)' : 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: (!selectedOrgId || submitting) ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshCw size={14} style={submitting ? { animation: 'spin 1s linear infinite' } : {}} />
            {submitting ? 'Running…' : 'Run Reprocess'}
          </button>
        </div>
      </div>

      {/* Preview results */}
      {previewMeetings !== null && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
              PREVIEW — {previewOrgName}
            </h2>
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
              {previewMeetings.length} meeting{previewMeetings.length !== 1 ? 's' : ''} would be reprocessed
              {silentCount > 0 && ` · ${silentCount} silent`}
              {aiErrCount > 0 && ` · ${aiErrCount} AI error`}
            </span>
          </div>

          {previewMeetings.length === 0 ? (
            <div style={{
              padding: '14px 16px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}>
              No reprocessable meetings found — either all extractions produced output or had legitimate-empty notes.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {previewMeetings.map((m, i) => (
                <div key={m.meetingId} style={{
                  padding: '12px 16px',
                  background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg)',
                  borderBottom: i < previewMeetings.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', gap: 12 }}>
                        {m.date && <span>Meeting: {fmtDate(m.date)}</span>}
                        {m.processedAt && <span>Processed: {fmtDate(m.processedAt)}</span>}
                        <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-dim)' }}>{m.meetingId.slice(0, 16)}…</span>
                      </div>
                      {m.extractionNote && (
                        <div style={{
                          marginTop: 6,
                          fontSize: 11,
                          color: '#f87171',
                          background: 'rgba(248,113,113,0.08)',
                          border: '1px solid rgba(248,113,113,0.2)',
                          borderRadius: 4,
                          padding: '4px 8px',
                          fontFamily: 'monospace',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {m.extractionNote}
                        </div>
                      )}
                    </div>
                    <NoteTag note={m.extractionNote} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.05em' }}>
            RECENT RUNS
          </h2>
          {history.map((r, i) => (
            <div key={i} style={{
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 8,
              border: `1px solid ${r.reset < 0 ? 'var(--danger)' : r.reset === 0 ? 'var(--border)' : 'var(--success)'}`,
              background: r.reset < 0 ? 'var(--danger-subtle)' : 'var(--bg-card)',
              fontSize: 13,
            }}>
              {r.reset < 0 ? (
                <div style={{ display: 'flex', gap: 8, color: 'var(--danger)' }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>{r.orgId}</strong> — Error: {r.message}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 8 }}>{new Date(r.ts).toLocaleTimeString()}</span>
                  </div>
                </div>
              ) : r.reset === 0 ? (
                <div style={{ color: 'var(--text-muted)' }}>
                  <strong>{r.orgName ?? r.orgId}</strong> — No reprocessable meetings found.
                  <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 8 }}>{new Date(r.ts).toLocaleTimeString()}</span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, color: 'var(--success)' }}>
                  <CheckCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>{r.orgName ?? r.orgId}</strong> — Reset {r.reset} meeting{r.reset !== 1 ? 's' : ''}.
                    Job <code style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.jobId}</code> running from {r.fromDate}.
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 8 }}>{new Date(r.ts).toLocaleTimeString()}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
